import React from 'react';

type LogoVariant = 'full' | 'text' | 'icon';
type LogoSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface ScoutMindLogoProps {
  variant?: LogoVariant;
  size?: LogoSize;
  className?: string;
  textColor?: string;
}

const ScoutMindLogo: React.FC<ScoutMindLogoProps> = ({
  variant = 'full',
  size = 'md',
  className = '',
  textColor = 'text-white'
}) => {
  const sizeClasses = {
    sm: {
      text: 'text-lg',
      icon: 'w-6 h-6'
    },
    md: {
      text: 'text-xl',
      icon: 'w-8 h-8'
    },
    lg: {
      text: 'text-2xl',
      icon: 'w-10 h-10'
    },
    xl: {
      text: 'text-3xl',
      icon: 'w-12 h-12'
    },
    '2xl': {
      text: 'text-4xl md:text-5xl lg:text-6xl',
      icon: 'w-16 h-16 md:w-20 md:h-20'
    }
  };

  const currentSize = sizeClasses[size];

  // Componente del icono/símbolo
  const LogoIcon = () => (
    <div className={`${currentSize.icon} relative flex items-center justify-center`}>
      {/* Círculo de fondo con degradado ScoutMind */}
      <div className="absolute inset-0 bg-gradient-scoutmind-light rounded-full opacity-20"></div>
      {/* Símbolo principal - representando análisis y scouting */}
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className={`${currentSize.icon} relative z-10`}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Círculo exterior */}
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="1.5"
          className="text-white"
        />
        {/* Elementos de análisis - líneas conectadas */}
        <path
          d="M8 8L12 12L16 8M8 16L12 12L16 16"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-white"
        />
        {/* Punto central */}
        <circle
          cx="12"
          cy="12"
          r="2"
          fill="currentColor"
          className="text-white"
        />
        {/* Puntos de datos */}
        <circle cx="8" cy="8" r="1" fill="currentColor" className="text-white" />
        <circle cx="16" cy="8" r="1" fill="currentColor" className="text-white" />
        <circle cx="8" cy="16" r="1" fill="currentColor" className="text-white" />
        <circle cx="16" cy="16" r="1" fill="currentColor" className="text-white" />
      </svg>
    </div>
  );

  // Componente del texto
  const LogoText = () => (
    <span className={`${currentSize.text} font-bold ${textColor}`}>
      ScoutMind
      <span className="text-blue-300 ml-1">AI</span>
    </span>
  );

  // Renderizado según la variante
  if (variant === 'icon') {
    return (
      <div className={`inline-flex items-center justify-center ${className}`}>
        <LogoIcon />
      </div>
    );
  }

  if (variant === 'text') {
    return (
      <div className={`inline-flex items-center ${className}`}>
        <LogoText />
      </div>
    );
  }

  // Variante 'full' - icono + texto
  return (
    <div className={`inline-flex items-center space-x-3 ${className}`}>
      <LogoIcon />
      <LogoText />
    </div>
  );
};

export default ScoutMindLogo;
